import TwoColumnContent from '../../../components/TwoColumnContent/TwoColumnContent';
import LeftColumn from '../../../components/TwoColumnContent/LeftColumn/LeftColumn';
import TitleDescription from '../../../components/TitleDescription/TitleDescription';
import RightColumn from '../../../components/TwoColumnContent/RightColumn/RightColumn';
import styles from '../EditTeamMember.module.css';
import { AdminInput } from '@project/components';
import UsersPropertyRolesList from '../../../components/UsersPropertyRolesList/UsersPropertyRolesList';
import React, { useEffect, useState } from 'react';
import { USER_STATUSES as statusOptions } from '../../../constants';
import { useFilteredProperties } from '../../../utils/hooks';
import { useEditTeamMember } from '../EditTeamMemberContext';
import { useIntl } from 'react-intl';
import axios from '../../../utils/axios';

export const EditTeamMemberProperties = () => {
  const intl = useIntl();
  const t = id => intl.formatMessage({ id });
  const {
    user,
    setUser,
    companyRole,
    setIsDirty,
    globalAccess,
    setGlobalAccess,
    companyLevelAcl,
    setCompanyLevelAcl,
    properties,
    setProperties,
    isLoaded,
    setLoading,
    setErrors,
    setIsLoaded,
    acls
  } = useEditTeamMember();

  const [filter, setFilter] = useState('');

  const [allSelected, setAllSelected] = useState({
    viewer: false,
    editor: false,
    designer: false,
    'company-admin': false
  });

  const [filteredProperties] = useFilteredProperties({ filter, properties });

  useEffect(() => {
    if (companyRole && properties.length) {
      if (companyRole.value === 'company-admin') {
        setGlobalAccess(true);
        setUser({ ...user, companyAdmin: true });
      } else {
        setUser({ ...user, companyAdmin: false });
      }

      const updatedProperties = properties.map(p => ({
        ...p,
        roleId: companyRole.value
      }));
      setProperties(updatedProperties);
      selectAll(companyRole.value, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyRole]);

  useEffect(() => {
    if (companyLevelAcl) {
      setCompanyLevelAcl(prevState => ({
        ...prevState,
        status: !globalAccess ? statusOptions[1].value : statusOptions[0].value
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalAccess]);

  /*
   * get the user's company info, property list,
   * and all of the user's acls
   * */
  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);

        const result = await axios.get(`/properties/${user.company}`);
        if (result.status === 200) {
          const props = result.data.data
            .map(p => {
              if (!p.displayName) p.displayName = p.name;
              return p;
            })
            .sort((a, b) => (a.displayName > b.displayName ? 1 : -1));

          // set the role id for each property based on current acls
          props.forEach(p => {
            // default to no role
            p.roleId = '';
            // find relevant acl, property exists use it
            // use company level if not exists
            const acl = acls.find(acl => {
              return (acl.property === p.name || !acl.property === p.displayName) && acl.status === 'active';
            });

            if (acl) {
              p.roleId = acl.roleid;
            } else if (companyLevelAcl && companyLevelAcl.status === 'active') {
              p.roleId = companyLevelAcl.roleid;
            }
          });

          setProperties(props);
        }

        setIsLoaded(true);
      } catch (e) {
        if (e.response && e.response.data) {
          setErrors(`${t('error.serverData')} (${e.response.data.errors})`);
        } else {
          setErrors((e && e.message) || t('error.server'));
        }
      } finally {
        setLoading(false);
      }
    };

    if (user && properties.length < 1) {
      fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const selectAll = (roleId, all) => {
    if (isLoaded) setIsDirty(true);

    filteredProperties.forEach(p => {
      p.roleId = all ? roleId : null;
    });
    setProperties([...properties]);

    let sel = { ...allSelected };
    Object.keys(sel).forEach(key => {
      sel[key] = all ? key === roleId : false;
    });
    setAllSelected(sel);
  };

  const onPropertyRoleChange = (event, index, roleId) => {
    if (isLoaded) setIsDirty(true);
    if (filteredProperties[index].roleId !== roleId) {
      filteredProperties[index].roleId = roleId;
    } else {
      filteredProperties[index].roleId = '';
    }
    setProperties([...properties]);
  };

  return (
    <>
      {/* global? */}
      <TwoColumnContent bottomBorder={true}>
        <LeftColumn>
          <TitleDescription
            title={t('InviteTeamMember.globalAccess.title')}
            description={t('InviteTeamMember.globalAccess.desc')}
          />
        </LeftColumn>
        <RightColumn>
          <div className={`${styles.checkInput} ${styles.mfa} d-flex align-items-center`}>
            <label htmlFor="has-global-access" className="m-0">
              {t('InviteTeamMember.globalAccess.label')}
            </label>
            <input
              style={{ marginLeft: '20px' }}
              id={'has-global-access'}
              type="checkbox"
              checked={globalAccess}
              disabled={user.companyAdmin}
              onChange={e => {
                setIsDirty(true);
                setGlobalAccess(e.target.checked);
              }}
            />
          </div>
        </RightColumn>
      </TwoColumnContent>

      {/* PROPERTY ROLES */}
      <TwoColumnContent className="pb-4">
        <LeftColumn>
          <TitleDescription
            title={t('EditTeamMember.permissions.title')}
            description={t('EditTeamMember.permissions.details')}
          />
        </LeftColumn>
        <RightColumn padded={true}>
          {!globalAccess ? (
            <div className="pl-2">
              <AdminInput
                id="permissions"
                label={t('EditTeamMember.permissions.label')}
                type="text"
                placeholder={t('EditTeamMember.permissions.placeholder')}
                value={filter}
                onChange={e => setFilter(e.target.value)}
              />

              <UsersPropertyRolesList
                properties={filteredProperties}
                allSelectedViewer={allSelected.viewer}
                allSelectedEditor={allSelected.editor}
                allSelectedDesigner={allSelected.designer}
                allSelectedAdmin={allSelected['company-admin']}
                allowAdmin={true}
                onPropertyRoleChange={onPropertyRoleChange}
                selectAll={selectAll}
              />
            </div>
          ) : (
            <div className="pl-2">{t('InviteTeamMember.globalAccess.disable')}</div>
          )}
        </RightColumn>
      </TwoColumnContent>
    </>
  );
};
